import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, combineLatest, map, throttleTime } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GPSTools {

    private watchId: number | null = null;

    private coordsSubject = new BehaviorSubject<GeolocationCoordinates | null>(null);
    coords$ = this.coordsSubject.asObservable();

    private headingSubject = new BehaviorSubject<number | null>(null);
    heading$ = this.headingSubject.asObservable();

    private errorSubject = new BehaviorSubject<string | null>(null);
    error$ = this.errorSubject.pipe(throttleTime(1000, undefined, { leading: true, trailing: true }));


    private orientationSubject = new BehaviorSubject<any>(null);
    orientation$ = this.orientationSubject.asObservable();

    position$ = combineLatest([
        this.coords$,
        this.orientation$
    ]).pipe(
        map(([coords, orientation]) => ({
            coords,
            orientation
        })),
        throttleTime(1000, undefined, { leading: true, trailing: true })
    );

    orientationSupported = false;
    compassActivated = false;


    constructor(private zone: NgZone) { }

    getCurrentPosition() {
        if (!navigator.geolocation) {
            this.errorSubject.next('La géolocalisation n\'est pas supportée');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            pos => {
                this.zone.run(() => {
                    this.coordsSubject.next(pos.coords);
                    this.errorSubject.next(null);

                    if (pos.coords.heading !== null) {
                        this.headingSubject.next(Math.round(pos.coords.heading));
                    }
                });
            },
            err => this.zone.run(() => {
                this.errorSubject.next(this.getErrorMessage(err));
            }),
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    }

    async initCompass() {
        if (typeof window === 'undefined' || this.compassActivated) return;

        const anyDeviceOrientation: any = DeviceOrientationEvent;

        if (anyDeviceOrientation?.requestPermission) {
            try {
                const permission = await anyDeviceOrientation.requestPermission();
                if (permission !== 'granted') {
                    this.errorSubject.next('Permission boussole refusée');
                    return;
                }
            } catch {
                this.errorSubject.next('Impossible d\'activer la boussole');
                return;
            }
        }

        if (!this.compassActivated)
            window.addEventListener('deviceorientation', this.handleOrientation, true);
        this.orientationSupported = true;
        this.compassActivated = true;
    }

    startWatching() {
        if (!navigator.geolocation || this.watchId !== null) return;

        this.watchId = navigator.geolocation.watchPosition(
            pos => {
                this.zone.run(() => {
                    this.coordsSubject.next(pos.coords);

                    if (pos.coords.heading !== null) {
                        this.headingSubject.next(Math.round(pos.coords.heading));
                    }
                });
            },
            err => this.zone.run(() => {
                this.errorSubject.next(this.getErrorMessage(err));
            }),
            {
                enableHighAccuracy: true,
                maximumAge: 0
            }
        );
    }

    stopWatching() {
        if (this.watchId !== null) {
            navigator.geolocation.clearWatch(this.watchId);
            this.watchId = null;
        }
    }

    destroy() {
        this.stopWatching();
        window.removeEventListener('deviceorientation', this.handleOrientation, true);
        this.compassActivated = false;
    }

    private handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.alpha === null) return;

        let heading = 360 - event.alpha;
        // this.orientationSubject.next(heading);

        const anyEvent: any = event;
        if (anyEvent.webkitCompassHeading !== undefined) {
            heading = anyEvent.webkitCompassHeading;
        }

        this.zone.run(() => {
            this.orientationSubject.next(Math.round(heading));
        });
    };

    private getErrorMessage(err: GeolocationPositionError): string {
        switch (err.code) {
            case err.PERMISSION_DENIED:
                return 'Permission refusée pour accéder à la position';
            case err.POSITION_UNAVAILABLE:
                return 'Position indisponible';
            case err.TIMEOUT:
                return 'Temps de localisation dépassé';
            default:
                return 'Erreur inconnue';
        }
    }

    async checkGeolocationStatus(): Promise<'granted' | 'denied' | 'prompt'> {
        if (!navigator.permissions) {
            console.warn('Permissions API not supported in this browser');
            return 'prompt';
        }

        try {
            const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
            return permissionStatus.state; // 'granted', 'denied', or 'prompt'
        } catch (err) {
            console.error('Error checking geolocation permission', err);
            return 'prompt';
        }
    }


}
